import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, Overline, EASE } from "../components/motion";
import { COURSE_CATALOGUE } from "../data/content";
import { Clock, CircleDollarSign, CalendarDays, BadgeCheck, ArrowLeft, ArrowUpRight } from "lucide-react";

export const ALL_COURSES = COURSE_CATALOGUE.flatMap((g) =>
  g.courses.map((c) => ({ ...c, category: g.category }))
);

const CATEGORY_IMAGES = {
  "Cytech Bicycle Mechanic Training": "https://images.unsplash.com/photo-1673870861521-626b40f9657e?q=80&w=1600&auto=format&fit=crop",
  "Guide Training": "https://images.unsplash.com/photo-1633707167682-9068729bc84c?q=80&w=1600&auto=format&fit=crop",
  "Winter & Backcountry Skills": "https://images.unsplash.com/photo-1478700485868-972b69dc3fc4?q=80&w=1600&auto=format&fit=crop",
  "Wilderness Medicine": "https://images.unsplash.com/photo-1600813828722-cde42dc764d9?q=80&w=1600&auto=format&fit=crop",
  "Marketing & Media Courses": "https://images.unsplash.com/photo-1625690303837-654c9666d2d0?q=80&w=1600&auto=format&fit=crop",
  "Mountain Sport Technician Courses": "https://images.unsplash.com/photo-1417021423914-070979c8eb34?q=80&w=1600&auto=format&fit=crop",
};

export default function CourseDetailPage() {
  const { slug } = useParams();
  const course = ALL_COURSES.find((c) => c.slug === slug);

  if (!course) {
    return (
      <main className="pt-24 min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h1 className="font-serif text-4xl text-navy">Course not found</h1>
          <Link to="/programs" data-testid="course-not-found-back" className="mt-6 inline-flex items-center gap-2 text-sm text-accent">
            <ArrowLeft size={15} /> Back to all programs
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24" data-testid="course-detail-page">
      <section className="py-20 md:py-24 bg-navy text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
            <Link to="/programs" data-testid="course-back-link" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary-foreground/50 hover:text-gold transition-colors duration-300">
              <ArrowLeft size={14} /> All Programs & Courses
            </Link>
            <div className="mt-8">
              <Overline light>{course.category}</Overline>
            </div>
            <h1 className="mt-4 font-serif text-4xl md:text-6xl tracking-tight leading-[1.08] max-w-3xl">
              {course.name}
            </h1>
            <p className="mt-4 font-serif italic text-xl text-gold">{course.note}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-full h-full border border-gold/50 rounded-sm pointer-events-none" />
              <div className="overflow-hidden rounded-sm aspect-[21/9]">
                <img
                  src={CATEGORY_IMAGES[course.category]}
                  alt={course.category}
                  data-testid="course-banner-image"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>About This Course</Overline>
              <p className="mt-5 text-lg leading-relaxed text-navy/85 font-serif">{course.description}</p>
              <p className="mt-5 text-muted-foreground leading-relaxed">
                Like every course at Victoria Career College, training is delivered in small,
                focused classes with a strong emphasis on hands-on learning, practical
                problem-solving, and industry standards.
              </p>
              <Link
                to={`/contact?course=${encodeURIComponent(course.name)}`}
                data-testid="course-enquire-btn"
                className="group mt-10 inline-flex items-center gap-3 bg-navy text-primary-foreground px-8 py-4 rounded-sm text-sm tracking-wide transition-colors duration-300 hover:bg-accent"
              >
                Enquire About This Course
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="bg-secondary/40 border border-border rounded-sm p-8 space-y-7 lg:sticky lg:top-32" data-testid="course-facts">
                <h2 className="font-serif text-2xl text-navy">Course Facts</h2>
                {[
                  [Clock, "Duration", course.duration, "fact-duration"],
                  [CircleDollarSign, "Tuition", course.tuition, "fact-tuition"],
                  [CalendarDays, "Upcoming Dates", course.dates, "fact-dates"],
                  [BadgeCheck, "Certification", course.certification, "fact-certification"],
                ].map(([Icon, label, value, tid]) => (
                  <div key={tid} className="flex gap-4 items-start" data-testid={tid}>
                    <span className="mt-1 text-accent"><Icon size={18} /></span>
                    <div>
                      <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{label}</p>
                      <p className="mt-1.5 text-sm text-navy leading-relaxed">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="border-t border-border pt-12">
              <Overline>More in {course.category}</Overline>
              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {COURSE_CATALOGUE.find((g) => g.category === course.category)
                  .courses.filter((c) => c.slug !== course.slug)
                  .slice(0, 3)
                  .map((c) => (
                    <Link
                      key={c.slug}
                      to={`/courses/${c.slug}`}
                      data-testid={`related-course-${c.slug}`}
                      className="group border border-border rounded-sm p-5 hover:border-accent transition-colors duration-300"
                    >
                      <p className="font-medium text-navy text-sm transition-colors duration-300 group-hover:text-accent">{c.name}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{c.note}</p>
                    </Link>
                  ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
